import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Filter, TrendingUp, Trophy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { PlayerNameLink } from "../lib/playerLink";

const ROLE_META = {
  P: { label: "Portieri", color: "bg-amber-500", tw: "text-amber-400" },
  D: { label: "Difensori", color: "bg-sky-500", tw: "text-sky-400" },
  C: { label: "Centrocampisti", color: "bg-emerald-500", tw: "text-emerald-400" },
  A: { label: "Attaccanti", color: "bg-rose-500", tw: "text-rose-400" },
};

export default function RosaSinottica() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [roosterMap, setRoosterMap] = useState({});
  const [valueFilter, setValueFilter] = useState("ALL"); // ALL | UNDER | OVER | STAR
  const [minQA, setMinQA] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/magnati/rosa-sinottica");
        setData(r.data);
        // Batch resolve rooster IDs for all player names
        const names = new Set();
        (r.data?.teams || []).forEach(t => {
          Object.values(t.roster_by_role || {}).forEach(list => list.forEach(p => p.name && names.add(p.name)));
        });
        if (names.size > 0) {
          api.post("/magnati/rooster/resolve-batch", { names: Array.from(names) })
            .then(res => setRoosterMap(res.data?.map || {}))
            .catch(() => { /* silent */ });
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Compute per-role value rank from all players in the loaded data
  const valueRankMap = useMemo(() => {
    if (!data) return {};
    const byRole = { P: [], D: [], C: [], A: [] };
    (data.teams || []).forEach(t => {
      Object.entries(t.roster_by_role || {}).forEach(([role, list]) => {
        if (!byRole[role]) return;
        list.forEach(p => byRole[role].push({ ...p, _team: t.team_name }));
      });
    });
    const map = {};
    for (const role of Object.keys(byRole)) {
      const list = byRole[role].sort((a, b) => {
        const va = Number(a.quotazione_attuale || 0);
        const vb = Number(b.quotazione_attuale || 0);
        return vb - va;
      });
      list.forEach((p, idx) => {
        // Composite key since sinottica lacks player_id — use team+name
        const key = `${p._team}|${p.name}`;
        map[key] = { rank: idx + 1, total: list.length, role };
      });
    }
    return map;
  }, [data]);

  function getTier(rankInfo) {
    if (!rankInfo || !rankInfo.rank) return null;
    const { rank, total } = rankInfo;
    if (rank === 1) return { emoji: "⭐⭐⭐", cls: "text-amber-300", title: `#1 di ${total}` };
    if (rank <= 3) return { emoji: "⭐⭐", cls: "text-amber-400", title: `#${rank} di ${total}` };
    const cutoff = Math.max(5, Math.floor(total * 0.1));
    if (rank <= cutoff) return { emoji: "⭐", cls: "text-amber-500/80", title: `#${rank} di ${total}` };
    return null;
  }

  const teams = data?.teams || [];

  const overallStats = useMemo(() => {
    if (!teams.length) return null;
    const values = teams.map(t => t.totals.value);
    const historic = teams.map(t => t.historic).filter(Boolean);
    const totSpent = historic.reduce((s, h) => s + (h.spent || 0), 0);
    const totEarned = historic.reduce((s, h) => s + (h.earned || 0), 0);
    const totAcq = historic.reduce((s, h) => s + (h.acquisitions_count || 0), 0);
    // Top spender
    const topSpender = historic.length
      ? teams.slice().filter(t => t.historic).sort((a, b) => (b.historic.spent || 0) - (a.historic.spent || 0))[0]
      : null;
    return {
      totalPlayers: teams.reduce((s, t) => s + t.totals.players, 0),
      avgValue: values.reduce((a, b) => a + b, 0) / teams.length,
      maxValue: Math.max(...values),
      maxTeam: teams.find(t => t.totals.value === Math.max(...values))?.name,
      minValue: Math.min(...values),
      minTeam: teams.find(t => t.totals.value === Math.min(...values))?.name,
      totalHistoricSpent: totSpent,
      totalHistoricEarned: totEarned,
      totalHistoricAcquisitions: totAcq,
      topSpender,
    };
  }, [teams]);

  function matchesFilters(p) {
    if (roleFilter !== "ALL" && p.role !== roleFilter) return false;
    if (search.trim() && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    const qa = Number(p.quotazione_attuale) || 0;
    const cost = Number(p.prezzo_pagato) || 0;
    if (minQA && qa < Number(minQA)) return false;
    if (valueFilter === "UNDER") {
      // Sottovalutato: Q.A. Serie A ≥ 20% sopra il costo pagato
      if (!(qa >= cost * 1.2 && cost > 0)) return false;
    } else if (valueFilter === "OVER") {
      // Sopravvalutato: costo pagato > Q.A. Serie A × 1.2 (ha pagato > +20% del valore)
      if (!(cost > qa * 1.2 && qa > 0)) return false;
    } else if (valueFilter === "STAR") {
      // Top-tier per ruolo (⭐ o migliori dal getTier)
      // Sarà filtrato successivamente nel render dove hai valueRankMap
      // qui lasciamo passare, il match effettivo avviene sotto
    }
    return true;
  }

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500 uppercase tracking-widest text-sm font-mono">Caricamento rose…</div>;
  if (!data) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500">Nessun dato</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <header className="border-b border-amber-500/20 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/storico-magnati" className="p-2 rounded-lg hover:bg-slate-800 text-slate-400" data-testid="sinottica-back-btn">
              <ArrowLeft className="w-5 h-5"/>
            </Link>
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-amber-500/70">Vista comparativa · Stagione {data.season_label}</div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500">
                Rosa Sinottica
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="../movimenti"
              data-testid="sinottica-open-rankings"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 text-xs font-black uppercase tracking-widest transition-colors"
            >
              🏆 Classifica Movimenti
            </Link>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 hidden sm:flex">
              {teams.length} club · {overallStats?.totalPlayers} giocatori
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">
        {/* Global stats */}
        {overallStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Giocatori totali</div>
                <div className="text-2xl font-bold text-slate-200">{overallStats.totalPlayers}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Valore medio rosa</div>
                <div className="text-2xl font-bold text-amber-300">{overallStats.avgValue.toFixed(0)}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500/20 to-slate-900 border-emerald-500/40">
              <CardContent className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Rosa più forte</div>
                <div className="text-sm font-bold text-emerald-300 truncate">{overallStats.maxTeam}</div>
                <div className="text-xs text-slate-400 font-mono">{overallStats.maxValue.toFixed(0)}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-rose-500/20 to-slate-900 border-rose-500/40">
              <CardContent className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-rose-400">Rosa più debole</div>
                <div className="text-sm font-bold text-rose-300 truncate">{overallStats.minTeam}</div>
                <div className="text-xs text-slate-400 font-mono">{overallStats.minValue.toFixed(0)}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Historic KPIs — Iter 71.3 */}
        {overallStats && overallStats.totalHistoricSpent > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="historic-global-kpis">
            <Card className="bg-gradient-to-br from-emerald-500/15 to-slate-900 border-emerald-500/30">
              <CardContent className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">💸 Speso Storico Totale</div>
                <div className="text-2xl font-bold text-emerald-300 font-mono">{overallStats.totalHistoricSpent.toFixed(0)}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">crediti fanta in acquisti</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-sky-500/15 to-slate-900 border-sky-500/30">
              <CardContent className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-sky-400">💰 Incassato Storico</div>
                <div className="text-2xl font-bold text-sky-300 font-mono">{overallStats.totalHistoricEarned.toFixed(0)}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">crediti da cessioni</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">🎯 Totale Acquisti</div>
                <div className="text-2xl font-bold text-slate-200 font-mono">{overallStats.totalHistoricAcquisitions}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">transazioni in ingresso</div>
              </CardContent>
            </Card>
            {overallStats.topSpender && (
              <Card className="bg-gradient-to-br from-amber-500/15 to-slate-900 border-amber-500/30">
                <CardContent className="p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400">🏦 Top Spender</div>
                  <div className="text-sm font-bold text-amber-300 truncate">{overallStats.topSpender.name}</div>
                  <div className="text-xs text-slate-400 font-mono">{overallStats.topSpender.historic.spent.toFixed(0)} cr · {overallStats.topSpender.historic.acquisitions_count} acq.</div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Filters */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-slate-500"/>
              <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Filtri</span>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setRoleFilter("ALL")}
                data-testid="filter-role-all"
                className={`px-3 py-1 rounded-md text-xs font-mono uppercase tracking-widest transition-colors ${
                  roleFilter === "ALL" ? "bg-amber-500 text-slate-950 font-bold" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                }`}>Tutti</button>
              {Object.entries(ROLE_META).map(([r, m]) => (
                <button key={r} onClick={() => setRoleFilter(r)}
                  data-testid={`filter-role-${r}`}
                  className={`px-3 py-1 rounded-md text-xs font-mono uppercase tracking-widest transition-colors ${
                    roleFilter === r ? "bg-amber-500 text-slate-950 font-bold" : `bg-slate-800 ${m.tw} hover:bg-slate-700`
                  }`}>{r}</button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Cerca giocatore…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="sinottica-search"
              className="flex-1 min-w-[200px] bg-slate-950 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-amber-500 focus:outline-none"
            />

            {/* Filtri avanzati: valore + Q.A. min */}
            <div className="w-full flex flex-wrap items-center gap-2 pt-1">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Mercato</span>
              <div className="flex gap-1">
                <button onClick={() => setValueFilter("ALL")}
                  data-testid="filter-value-all"
                  className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest transition-colors ${
                    valueFilter === "ALL" ? "bg-slate-700 text-slate-100 font-bold" : "bg-slate-800/60 text-slate-500 hover:bg-slate-700"
                  }`}>Tutti</button>
                <button onClick={() => setValueFilter("UNDER")}
                  data-testid="filter-value-under"
                  title="Q.A. Serie A ≥ 20% sopra il prezzo pagato"
                  className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest transition-colors ${
                    valueFilter === "UNDER" ? "bg-emerald-500 text-slate-950 font-bold" : "bg-slate-800/60 text-emerald-400 hover:bg-slate-700"
                  }`}>📈 Sottovalutati</button>
                <button onClick={() => setValueFilter("OVER")}
                  data-testid="filter-value-over"
                  title="Prezzo pagato ≥ 20% sopra la Q.A. Serie A"
                  className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest transition-colors ${
                    valueFilter === "OVER" ? "bg-rose-500 text-slate-950 font-bold" : "bg-slate-800/60 text-rose-400 hover:bg-slate-700"
                  }`}>📉 Sopravvalutati</button>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Q.A. ≥</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={minQA}
                  onChange={e => setMinQA(e.target.value)}
                  data-testid="filter-min-qa"
                  className="w-16 bg-slate-950 border border-slate-700 rounded-md px-2 py-1 text-xs text-slate-100 focus:border-amber-500 focus:outline-none font-mono"
                  placeholder="0"
                />
              </div>
              {(valueFilter !== "ALL" || minQA) && (
                <button
                  onClick={() => { setValueFilter("ALL"); setMinQA(""); }}
                  data-testid="filter-clear"
                  className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-white underline"
                >
                  Reset filtri
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Grid: 5 columns x 2 rows on big screen, adaptive */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {teams.map((team, idx) => {
            const roles = ["P","D","C","A"];
            const roleGroups = roleFilter === "ALL" ? roles : [roleFilter];
            return (
              <Card key={team.team_id}
                data-testid={`sinottica-team-${team.magnati_club_id}`}
                className={`bg-slate-900/60 border-slate-800 ${idx === 0 ? "ring-2 ring-emerald-500/40" : ""}`}>
                <CardHeader className="p-3 border-b border-slate-800">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs font-mono ${idx === 0 ? "text-emerald-400" : "text-slate-500"}`}>#{idx + 1}</span>
                        <Link to={team.magnati_club_id ? `/storico-magnati/club/${team.magnati_club_id}` : "#"}
                          className="text-sm font-bold text-slate-100 hover:text-amber-300 truncate">
                          {team.name}
                        </Link>
                      </div>
                      <div className="text-[10px] text-slate-500 truncate mt-0.5">
                        👤 {team.current_manager || team.expected_manager || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[9px] font-mono uppercase text-slate-600">Val</div>
                      <div className="text-sm font-bold text-amber-300">{team.totals.value.toFixed(0)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-1 mt-2">
                    {roles.map(r => (
                      <div key={r} className="text-center">
                        <div className={`text-[9px] font-mono ${ROLE_META[r].tw}`}>{r}</div>
                        <div className="text-xs font-bold text-slate-300">{team.roster_by_role[r]?.length || 0}</div>
                      </div>
                    ))}
                  </div>
                  {team.historic && (
                    <div className="mt-2 pt-2 border-t border-slate-800 grid grid-cols-3 gap-1 text-center" data-testid={`historic-${team.magnati_club_id}`}>
                      <div title={team.historic.top_purchase ? `Acquisto top: ${team.historic.top_purchase.player_name} · ${team.historic.top_purchase.cost} cr in ${team.historic.top_purchase.season}` : ""}>
                        <div className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Speso Storico</div>
                        <div className="text-xs font-black text-emerald-300 font-mono">{team.historic.spent.toFixed(0)}</div>
                        <div className="text-[9px] text-slate-600">{team.historic.acquisitions_count} acq.</div>
                      </div>
                      <div title={team.historic.top_sale ? `Vendita top: ${team.historic.top_sale.player_name} · ${team.historic.top_sale.cost} cr in ${team.historic.top_sale.season}` : ""}>
                        <div className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Incassato</div>
                        <div className="text-xs font-black text-sky-300 font-mono">{team.historic.earned.toFixed(0)}</div>
                        <div className="text-[9px] text-slate-600">{team.historic.sales_count} cess.</div>
                      </div>
                      <div>
                        <div className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Netto</div>
                        <div className={`text-xs font-black font-mono ${team.historic.net > 0 ? "text-rose-300" : "text-emerald-300"}`}>
                          {team.historic.net > 0 ? "-" : "+"}{Math.abs(team.historic.net).toFixed(0)}
                        </div>
                        <div className="text-[9px] text-slate-600">{team.historic.net > 0 ? "investito" : "guadagno"}</div>
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-2 space-y-2 max-h-[560px] overflow-y-auto">
                  {roleGroups.map(role => {
                    const players = (team.roster_by_role[role] || []).filter(matchesFilters);
                    if (players.length === 0 && roleFilter !== "ALL") return null;
                    return (
                      <div key={role}>
                        {roleFilter === "ALL" && (
                          <div className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded mb-1 ${ROLE_META[role].tw}`}>
                            {ROLE_META[role].label} · {(team.roster_by_role[role] || []).length}
                          </div>
                        )}
                        {players.length === 0 && search ? (
                          <div className="text-[10px] text-slate-600 italic px-2">—</div>
                        ) : players.map((p, i) => {
                          const rid = roosterMap[p.name];
                          const tier = getTier(valueRankMap[`${team.team_name}|${p.name}`]);
                          const qa = Number(p.quotazione_attuale) || 0;
                          const cost = Number(p.prezzo_pagato) || 0;
                          const isUnder = qa >= cost * 1.2 && cost > 0;
                          const isOver = cost > qa * 1.2 && qa > 0;
                          return (
                          <div key={i} className="px-2 py-1 rounded hover:bg-slate-800/60 text-xs flex items-center justify-between gap-1">
                            <div className="min-w-0 flex-1">
                              <div className="text-slate-200 truncate flex items-center gap-1.5">
                                <span className={`inline-block w-1 h-3 ${ROLE_META[role].color} rounded-full`}></span>
                                {tier && <span className={`text-[9px] ${tier.cls} shrink-0`} title={tier.title}>{tier.emoji}</span>}
                                <PlayerNameLink
                                  name={p.name}
                                  fantaId={p.fanta_id}
                                  roosterMap={roosterMap}
                                  className="truncate"
                                  testid={`sinottica-history-${p.name.replace(/\s+/g,'_')}`}/>
                                {isUnder && <span className="text-[9px] text-emerald-400 shrink-0" title="Sottovalutato: Q.A. ≥ 20% > costo">📈</span>}
                                {isOver && <span className="text-[9px] text-rose-400 shrink-0" title="Sopravvalutato: costo ≥ 20% > Q.A.">📉</span>}
                                {(p.fanta_id || rid) && !isUnder && !isOver && <span className="text-[8px] text-amber-500/70" title="Storia disponibile">📖</span>}
                              </div>
                              <div className="text-[9px] text-slate-600 uppercase font-mono ml-2.5">{p.real_team}</div>
                            </div>
                            <div className="shrink-0 text-right">
                              <div className={`text-[10px] font-mono font-bold ${isUnder ? "text-emerald-300" : isOver ? "text-rose-300" : "text-amber-300"}`}>
                                {qa.toFixed(0)}
                              </div>
                              <div className="text-[9px] text-slate-600 font-mono">c{cost}</div>
                            </div>
                          </div>
                        );})}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
